/**
 * Panel Sync Hook
 * 
 * Synchronizes panel configurations with backend on authentication state changes.
 * Loads user's saved panel layouts on login, saves on logout.
 * 
 * @version 1.2.0
 * @date January 13, 2026
 * @task T-S24-003
 * 
 * Peer Review: Gemini 9.5/10, GPT 9.7/10 → Final polish applied
 * 
 * Features:
 * - Load panels from API on login
 * - Save panels on logout/session expiry
 * - Save panels before page unload (fetch with keepalive)
 * - Fallback to localStorage if API unavailable
 * - isMounted guard for async safety
 * - Payload size check for keepalive limit (64KB)
 */

import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../stores/authStore';
import usePanelConfigStore, { PanelState } from '../stores/panelConfigStore';
import { isDemoModeEnabled, shouldConnectToApi } from '../utils/environment';

// ============================================================================
// TYPES
// ============================================================================

interface PanelApiPayload {
  panel_id: string;
  panel_type: string;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  is_visible: boolean;
  is_minimized: boolean;
  is_maximized: boolean;
  z_index: number;
  config: Record<string, unknown>;
  view_context: string;
}

// ============================================================================
// HELPERS (Pure functions outside component for reference stability)
// ============================================================================

/**
 * Format panels for API payload
 * Defined outside component to ensure reference stability
 */
function formatPanelsForApi(
  panels: Record<string, PanelState> | undefined,
  viewContext: string
): PanelApiPayload[] {
  if (!panels) return [];
  
  return Object.values(panels).map((p) => ({
    panel_id: p.panelId,
    panel_type: p.panelType,
    position_x: p.position.x,
    position_y: p.position.y,
    width: p.size.width,
    height: p.size.height,
    is_visible: p.isVisible,
    is_minimized: p.isMinimized,
    is_maximized: p.isMaximized,
    z_index: p.zIndex,
    config: p.config,
    view_context: viewContext,
  }));
}

// Keepalive payload limit (browsers typically enforce ~64KB)
const KEEPALIVE_LIMIT_BYTES = 64000;

// ============================================================================
// HOOK
// ============================================================================

export interface UsePanelSyncResult {
  isLoading: boolean;
  isSaving: boolean;
  lastSyncedAt: string | null;
  error: string | null;
  isAuthenticated: boolean;
  hasLoaded: boolean;
}

/**
 * Hook to sync panel configurations with backend on auth state changes.
 * 
 * Usage: Call once at app root level (e.g., in App.tsx or a layout component)
 * 
 * Behavior:
 * - On login (unauthenticated → authenticated): Load panels from API
 * - On logout (authenticated → unauthenticated): Save current panels
 * - On session expiry: Save current panels
 * - On page unload: Save current panels via fetch with keepalive
 * 
 * @returns Object with sync status
 */
export function usePanelSync(): UsePanelSyncResult {
  const status = useAuthStore((state) => state.status);
  const token = useAuthStore((state) => state.token);
  const hasHydrated = useAuthStore((state) => state.hasHydrated);
  
  const {
    loadFromApi,
    saveToApi,
    setApiAvailable,
    isLoading,
    isSaving,
    lastSyncedAt,
    error,
    currentView,
    configsByView,
  } = usePanelConfigStore();
  
  // Track previous auth status to detect transitions
  const prevStatusRef = useRef<typeof status | null>(null);
  
  // Use useState for hasLoaded so it's reactive
  const [hasLoaded, setHasLoaded] = useState(false);
  
  // Ref to hold latest values for beforeunload handler (avoids stale closures)
  const syncRef = useRef({
    configsByView,
    currentView,
    token,
    hasLoaded,
  });
  
  // Keep syncRef updated with latest values (runs after every render)
  useEffect(() => {
    syncRef.current = {
      configsByView,
      currentView,
      token,
      hasLoaded,
    };
  }, [configsByView, currentView, token, hasLoaded]);
  
  // Sync panels on auth state transitions
  useEffect(() => {
    // Wait for auth store to hydrate
    if (!hasHydrated) return;
    
    // isMounted flag for async safety (prevents state updates after unmount)
    let isMounted = true;
    
    const prevStatus = prevStatusRef.current;
    prevStatusRef.current = status;
    
    // Skip if status hasn't changed
    if (prevStatus === status) return;
    
    // Handle state transitions
    const handleTransition = async () => {
      // Login: unauthenticated/loading → authenticated
      if (status === 'authenticated' && prevStatus !== 'authenticated') {
        console.log('[PanelSync] User authenticated, loading panel configurations...');
        
        // CRITICAL: In demo mode, skip API calls entirely - use localStorage only
        if (isDemoModeEnabled() || !shouldConnectToApi()) {
          console.log('[PanelSync] Demo mode enabled - using local storage only (no API calls)');
          if (isMounted) {
            setApiAvailable(false);
            setHasLoaded(true);
          }
          return; // Exit early - no API call
        }
        
        try {
          if (isMounted) setApiAvailable(true);
          await loadFromApi();
          if (isMounted) {
            setHasLoaded(true);
            console.log('[PanelSync] Panel configurations loaded successfully');
          }
        } catch (err) {
          console.warn('[PanelSync] Failed to load from API, using local storage:', err);
          if (isMounted) {
            // API unavailable - panels will use localStorage fallback (handled by persist middleware)
            setApiAvailable(false);
            // Still mark as loaded since localStorage has data
            setHasLoaded(true);
          }
        }
      }
      
      // Logout: authenticated → unauthenticated
      if (status === 'unauthenticated' && prevStatus === 'authenticated') {
        console.log('[PanelSync] User logged out, saving panel configurations...');
        
        // Use hasLoaded state (not ref) since we're in the effect scope
        if (hasLoaded) {
          // Fire-and-forget save on logout (don't await to avoid blocking)
          saveToApi(currentView).catch((err) => {
            console.warn('[PanelSync] Failed to save on logout:', err);
          });
        }
        
        if (isMounted) setHasLoaded(false);
      }
      
      // Session expired: save panels
      if (status === 'session_expired' && prevStatus === 'authenticated') {
        console.log('[PanelSync] Session expired, saving panel configurations...');
        
        if (hasLoaded) {
          saveToApi(currentView).catch((err) => {
            console.warn('[PanelSync] Failed to save on session expiry:', err);
          });
        }
      }
    };
    
    handleTransition();
    
    // Cleanup: mark unmounted to prevent state updates
    return () => {
      isMounted = false;
    };
  }, [status, hasHydrated, hasLoaded, loadFromApi, saveToApi, setApiAvailable, currentView]);
  
  // Save panels before page unload using fetch with keepalive
  // Empty dependency array: uses syncRef for latest values, no re-binding needed
  useEffect(() => {
    const handleBeforeUnload = () => {
      // CRITICAL: Skip all API calls in demo mode
      if (isDemoModeEnabled() || !shouldConnectToApi()) {
        return; // Demo mode - localStorage persistence only
      }
      
      const {
        configsByView: currentConfigs,
        currentView: view,
        token: currentToken,
        hasLoaded: loaded,
      } = syncRef.current;
      
      // Only save if authenticated and loaded
      if (!currentToken?.accessToken || !loaded) return;
      
      const panels = currentConfigs[view];
      if (!panels || Object.keys(panels).length === 0) return;
      
      const apiUrl = import.meta.env.VITE_MDC_API_URL || '';
      
      // Guard against missing API URL
      if (!apiUrl) {
        console.warn('[PanelSync] VITE_MDC_API_URL not set, skipping unload save');
        return;
      }
      
      const endpoint = `${apiUrl}/api/v1/panel-configurations/batch`;
      const payload = formatPanelsForApi(panels, view);
      const body = JSON.stringify(payload);
      
      // Check payload size (keepalive has ~64KB limit)
      const payloadSize = new Blob([body]).size;
      if (payloadSize > KEEPALIVE_LIMIT_BYTES) {
        console.warn(
          `[PanelSync] Payload too large for keepalive (${payloadSize} bytes > ${KEEPALIVE_LIMIT_BYTES}). ` +
          'Data may not be saved on page close.'
        );
        // Still attempt - browser may accept or reject
      }
      
      // Use fetch with keepalive: true instead of sendBeacon
      // This allows custom headers (Authorization) and survives page unload
      try {
        fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentToken.accessToken}`,
          },
          body,
          keepalive: true, // Critical: allows request to complete after page unload
        }).catch((err) => {
          // Fire-and-forget, but log errors for debugging
          console.error('[PanelSync] Unload save failed:', err);
        });
      } catch (err) {
        console.error('[PanelSync] Failed to initiate unload save:', err);
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []); // Empty deps: relies on syncRef for latest values
  
  return {
    isLoading,
    isSaving,
    lastSyncedAt,
    error,
    isAuthenticated: status === 'authenticated',
    hasLoaded,
  };
}

export default usePanelSync;
